import React, {useContext, useEffect, useState} from "react"
import {Button, Col, Container, Modal, ModalBody, Row,} from "reactstrap"
import Pagination from "../../components/Pagination"
import {useRouter} from "next/router";
import SEARCH_COTTAGES from "../../graphql/queries/cottageSearch";
import {useQuery} from "@apollo/client";
import withApollo from "../../lib/withApollo";
import {getDataFromTree} from "@apollo/react-ssr";
import CottageCard from "../../components/CottageSearch/CottageCard";
import Head from "next/head";
import {FilterSelection} from "../../types/filterSelection";
import dynamic from "next/dynamic";
import SmallScreenComponent from "../../components/SmallScreenComponent";
import LargeScreenComponent from "../../components/LargeScreenComponent";
import EmailUpdatesButton from "../../components/CottageSearch/EmailUpdatesButton";
import CottageCardWithPreloadedImage from "../../components/CottageSearch/CottageCardWithPreloadedImage";
import {Context} from "../../lib/Store";
import ModalHeader from "../../components/Modal/ModalHeader";
import getSnippetFor from "../../lib/richFAQSnippentProviderForCountry";
import FAQ from "../../components/Destination/FAQ";

const Map = dynamic(() => import("../../components/CottageSearch/Map"), {
    ssr: false,
})

const findDestinationDescription = (destination: string) => {
    switch (destination) {
        case 'engeland':
            return {
                title: 'Vakantiehuizen Engeland',
                text: 'Engeland is een enorm gevarieerd land dat niet in een paar woorden te beschrijven valt. Iedere regio heeft zijn eigen karakter. Wat wel in heel Engeland geldt is dat het land barst van de historie en natuurschoon. Afhankelijk van uw wensen kunt u een van de vele regio’s gaan verkennen. Doe dit eens vanuit uw eigen vakantiewoning, waar u na een volle dag met veel nieuwe indrukken heerlijk bij de open haard tot rust kunt komen. Bekijk ons veelzijdige aanbod aan cottages en maak op uw eigen wijze kennis met het veelzijdige Engeland.'
            }
        case 'ierland':
            return {
                title: 'Vakantiehuizen Ierland',
                text: 'Ierland, land van 1000 tinten groen, van welkom en van ongerept natuurschoon. In Ierland komt de natuurliefhebber goed aan zijn trekken. Er zijn in heel het land meer dan genoeg wandelroutes en fietsroutes. De golfer kan van het vroege voorjaar tot de late herfst overal een prima partij golf spelen op een van de vele gerenommeerde golfcourses. Sportvissers kunnen veelvuldig vissen op de meren en rivieren, aan stranden en op zee. Surfers gaan naar Bundoran, een geheim surfplekje bij zowel professionals als amateurs.<br>' +
                    'Combineer meerdere regio’s en diverse cottages om het meeste uit uw vakantie te halen. '
            }
        case 'schotland':
            return {
                title: 'Vakantiehuizen Schotland',
                text: 'Het land van bergen, bruisende rivieren, schilderachtige kusten, diepe meren en gastvrije mensen. Tijdens uw vakantie vertellen deze gastvrije mensen u graag over hun geschiedenis en vele attracties en activiteiten in Schotland. <br>Ga dit prachtige land streek voor streek verkennen vanuit uw eigen vakantiewoning. Na een lange dag met veel nieuwe indrukken kunt bij de open haard, in uw zelf uitgekozen vakantiewoning, heerlijk bijkomen. U kunt er ook voor kiezen om tijdens uw vakantie een combinatie van vakantiewoningen in verschillende streken van Schotland te maken. Bekijk ons veelzijdige aanbod aan cottages en ga het prachtige Schotland zelf ontdekken.'
            }
        case 'wales':
            return {
                title: 'Vakantiehuizen Wales',
                text: 'Wales heeft een grote diversiteit aan landschappen en culturen. Ontdek de kust, de bergen en bezoek een van de kastelen – Wales heeft de hoogste kasteeldichtheid ter wereld. <br>' +
                    'Het is een land waar genoeg te doen is, met name in de natuur, en er zijn 3 Nationale Parken. U kunt in heel Wales goed wandelen, tuinen bezichtigen, vissen, fietsen, golfen, klimmen of paardrijden en zelfs surfen en trampolinespringen in grotten.  <br>' +
                    'Wales is te verdelen in 4 belangrijke regio\'s en iedere regio heeft zijn eigen charme en beleving. Het is dan ook zeker de moeite waard om een langere periode te gaan en daarbij in meerdere vakantiewoningen te verblijven. '
            }
        default:
            return null;
    }
};

const Index = () => {
    const [state, dispatch] = useContext(Context);
    const router = useRouter();
    const query = router.query;
    const destinationDescription = query.destination ? findDestinationDescription(query.destination[0]) : null;
    const searchSelection: FilterSelection = FilterSelection.fromUrlParams(router);
    const [hoverCard, setHoverCard] = React.useState(null)
    const [page, setPage] = useState(query['pagina'] ? parseInt(query['pagina'].toString()) : 1);
    const [mapView, setMapView] = useState(false);
    const {loading, error, data, client} = useQuery(SEARCH_COTTAGES, {
        // context: { clientName: 'turbo' },
        variables: {
            filter: searchSelection.toGraphQLFilterInput(),
            orderBy: "PRICE",
            paginator: {page: page, itemsPerPage: 16},
            limit: 16,
            offset: 0
        },
    });

    const [modal, setModal] = useState(false);
    const toggle = () => {
        setModal(!modal);
        setMapView(!mapView);
        const {urlPath, urlParams} = searchSelection.getPropertiesForLink();

        if (mapView) {
            // @ts-ignore
            dispatch({type: 'navigate_back'});
        }

        router.replace({
            pathname: urlPath,
            query: {
                ...urlParams,
                'toon-kaart': !mapView ? 1 : 0
            }
        });
    };

    useEffect(() => {
        const page = query['pagina'] ? parseInt(query['pagina'].toString()) : 1;
        setPage(page);

        if (query['toon-kaart'] === '1') {
            setMapView(true);
            setModal(true);
        }
    }, [query])

    const onCardEnter = (id) => {
        setHoverCard(id)
    }

    const onCardLeave = () => {
        setHoverCard(null)
    }

    if (!data) {
        return <div className="loading">Cottage's aan het ophalen...</div>;
    }

    const richFAQSnipped = query.destination ? getSnippetFor(query.destination.toString(), data.cottageSearch.totalHits) : null;
    const isPageUnDefined = query.pagina === undefined;

    return (
        <React.Fragment>
            <Head>
                <title>Vakantiehuis huren in {data.cottageSearch.searchInformation.destinationSummary ? data.cottageSearch.searchInformation.destinationSummary : 'Groot Brittannië'} - Cottage Centre
                    vakantiehuizen</title>
                <meta name="viewport" content="initial-scale=1.0, width=device-width"/>
                <meta
                    name="description"
                    content={`Vakantiehuis in ${data.cottageSearch.searchInformation.destinationSummary} huren? Cottagecentre is gespecialiseerd in vakantiehuizen in Groot-Brittannie en Ierland. Zoek en boek online.`}
                />
            </Head>
            {richFAQSnipped && isPageUnDefined &&
                <script type="application/ld+json" dangerouslySetInnerHTML={{__html: richFAQSnipped}}>
            </script>
            }
            <Container fluid>
                <Row>
                    <Col lg="6" className="py-4 p-xl-5 mt-lg-6 mt-0">
                        <EmailUpdatesButton searchInformation={data.cottageSearch.searchInformation}/>
                        <SmallScreenComponent>
                            <Button outline color="primary" size="sm" className="float-right"
                                    onClick={() => toggle()}>
                                {mapView ?
                                    <><i className="fas fa-list"/> Lijstweergave</>
                                    :
                                    <><i className="far fa-map"/> Kaartweergave</>}
                            </Button>
                        </SmallScreenComponent>
                        <h2 className="mb-4">
                            {data.cottageSearch.totalHits > 9999 && <><small>Meer dan</small> {data.cottageSearch.totalHits}</>}
                            {data.cottageSearch.totalHits <= 9999 && <>{data.cottageSearch.totalHits}</>}
                            {" "}<small>Cottages</small>
                        </h2>
                        <hr className="mb-4"/>
                        <Row>
                            {mapView &&
                                <SmallScreenComponent>
                                    <Modal toggle={toggle} fade={false} isOpen={modal}>
                                        <ModalHeader toggle={toggle}>
                                        </ModalHeader>
                                        <ModalBody className="modal-body-no-padding">
                                            <div className="map-side-lg">
                                        <Map
                                            className="map-full shadow-left"
                                            center={[51.505, -0.09]}
                                            zoom={10}
                                            hoverCard={hoverCard}
                                            filterSelection={searchSelection}
                                            searchInformation={data.cottageSearch.searchInformation}
                                        />
                                            </div>
                                        </ModalBody>
                                    </Modal>
                                </SmallScreenComponent>
                            }
                            {!mapView && data.cottageSearch.cottages.map((cottage, index) => (
                                <Col
                                    key={cottage.id}
                                    sm="6"
                                    className="mb-5"
                                    onMouseEnter={() => onCardEnter(cottage.slug)}
                                    onMouseLeave={() => onCardLeave()}
                                >
                                    <>
                                        {(index === 0 || index === 1) &&
                                            <CottageCardWithPreloadedImage
                                                cottageId={cottage.id}
                                                code={cottage.internalCode}
                                                priceForStay={cottage.priceForStay}
                                                priceInfo={cottage.priceInfo}
                                                startDate={cottage.startDate}
                                                endDate={cottage.endDate}
                                                slug={cottage.slug}
                                                name={cottage.name}
                                                tags={cottage.tags}
                                                description={cottage.dutchDescriptionTruncated}
                                                images={cottage.images.filter(image => image.online).map(image => image.url)}
                                                lowestWeekPrice={cottage.lowestWeekPrice}
                                                highestWeekPrice={cottage.highestWeekPrice}
                                                searchSelection={searchSelection}
                                                searchInformation={data.cottageSearch.searchInformation}
                                                isAutomaticallyTranslated={cottage.isDutchDescriptionAutoTranslated}
                                                county={cottage.county}
                                                country={cottage.country}
                                            />
                                        }

                                        {(index !== 0 && index !== 1) &&
                                            <CottageCard
                                                cottageId={cottage.id}
                                                code={cottage.internalCode}
                                                priceForStay={cottage.priceForStay}
                                                priceInfo={cottage.priceInfo}
                                                startDate={cottage.startDate}
                                                endDate={cottage.endDate}
                                                slug={cottage.slug}
                                                name={cottage.name}
                                                tags={cottage.tags}
                                                description={cottage.dutchDescriptionTruncated}
                                                images={cottage.images.filter(image => image.online).map(image => image.url)}
                                                lowestWeekPrice={cottage.lowestWeekPrice}
                                                highestWeekPrice={cottage.highestWeekPrice}
                                                searchSelection={searchSelection}
                                                searchInformation={data.cottageSearch.searchInformation}
                                                isAutomaticallyTranslated={cottage.isDutchDescriptionAutoTranslated}
                                                county={cottage.county}
                                                country={cottage.country}
                                            />
                                        }
                                    </>
                                </Col>
                            ))}
                        </Row>
                        <div className="d-none d-lg-block">
                            <Pagination
                                searchSelection={searchSelection}
                                pagination={data.cottageSearch.pagination}
                            />
                        </div>
                        <div className="d-lg-none">
                            {!mapView &&
                            <Pagination
                                searchSelection={searchSelection}
                                pagination={data.cottageSearch.paginationMobile}
                            />
                            }
                        </div>

                        {destinationDescription && isPageUnDefined &&
                        <div className="mt-4">
                            <h1>{destinationDescription.title}</h1>
                            <div dangerouslySetInnerHTML={{__html: destinationDescription.text}}/>
                        </div>
                        }

                        {isPageUnDefined && query.destination &&
                            <FAQ destination={query.destination.toString()} totalHits={data.cottageSearch.totalHits} />
                        }
                    </Col>

                    {!mapView &&
                    <Col lg="6" className="map-side-lg pr-lg-0">
                        <LargeScreenComponent>
                            <Map
                                className="map-full shadow-left"
                                center={[51.505, -0.09]}
                                zoom={10}
                                hoverCard={hoverCard}
                                filterSelection={searchSelection}
                                searchInformation={data.cottageSearch.searchInformation}
                            />
                        </LargeScreenComponent>
                    </Col>
                    }
                </Row>
            </Container>
        </React.Fragment>
    )
}

Index.getInitialProps = async () => {
    return {
        nav: {
            light: true,
            classes: "shadow",
            color: "white",
        },
        showCottageSearch: true,
    }
}

export default withApollo(Index, {getDataFromTree});
